import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo, useEffect, useMemo } from 'react';
import { CompanyService } from '../../../service/CompanyService';
import { CompanySpacesService } from '../../../service/CompanySpacesService';
import { UserService } from '../../../service/UserService';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';
import { additionalOptionsArray } from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';

interface MemoryPodFormData {
  query: string;
  searchResult: string;
  model: string;
  systemAi: string;
  topK: number;
  searchType: string;
  returnRaw: boolean;
}

type MemoryPodChangeEvent =
  | SelectChangeEvent<string>
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type Props = {
  onHide: () => void;
  show: boolean;
  formData: MemoryPodFormData;
  onChange: (event: MemoryPodChangeEvent) => void;
};

type TechModel = {
  type: string;
  isDeprecated?: boolean;
  model: string;
  displayName?: string;
  disabledByCompany?: boolean;
};

type TechSystem = {
  system: string;
  models?: TechModel[];
};

const SettingsModal = ({ onHide, show, formData: values, onChange }: Props) => {
  const {
    technologies,
    companySpaceTechnologies,
    setTechnologies,
    setCompanySpaceTechnologies,
    user,
  } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();

  useEffect(() => {
    if (!show) return;

    const fetchAllTechnologies = async () => {
      try {
        if (spaceId) {
          const res = await CompanySpacesService.getSpaceTechnologies(spaceId);
          setCompanySpaceTechnologies(res.data);
        }
        if (technologies.length === 0) {
          const data = user?.selectedCompanyId
            ? await CompanyService.getCompanyTechnologies(user.selectedCompanyId)
            : await UserService.getTechnologies();
          setTechnologies(data.data);
        }
      } catch (error) {
        console.log('Error fetching technologies:', error);
      }
    };

    fetchAllTechnologies();
  }, [spaceId, show, user?.selectedCompanyId]);

  const models = useMemo(() => {
    const systemsToUse: TechSystem[] =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    const openAiSystem = systemsToUse?.find(s => s.system === 'openAi');

    return (
      openAiSystem?.models
        ?.filter(m => m.type === 'Text' && !m.isDeprecated)
        ?.map(m => ({
          value: m.model,
          label: m.displayName || m.model,
          disabled: m.disabledByCompany ?? false,
        })) ?? []
    );
  }, [technologies, companySpaceTechnologies, spaceId]);

  const additionalOptionsArrayFiltered = useMemo(() => {
    return additionalOptionsArray.map(option => ({
      ...option,
      disabled: option.value !== 'OpenAI',
    }));
  }, []);

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '320px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Typography variant='h6'> Settings</Typography>

      <FormControl
        sx={{
          mt: '24px',
          mb: '24px',
        }}
        fullWidth
      >
        <InputLabel htmlFor={'searchType'}>Search Type</InputLabel>
        <Select
          fullWidth
          value={values.searchType}
          name='searchType'
          className='nodrag'
          label='Search Type'
          onChange={onChange}
        >
          <MenuItem value='similarity'>Similarity Search</MenuItem>
          <MenuItem value='similarity-ner'>Similarity Search (NER)</MenuItem>
        </Select>
      </FormControl>

      <FormControl
        fullWidth
        sx={{
          mb: '24px',
        }}
      >
        <InputLabel htmlFor='systemAi'>System </InputLabel>

        <Select
          name='systemAi'
          className='nodrag'
          label='System '
          fullWidth
          value={values.systemAi}
          onChange={onChange}
        >
          {additionalOptionsArrayFiltered.map(opt => (
            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel htmlFor='model'>Ai Model</InputLabel>
        <Select
          className='nodrag'
          value={values.model}
          name='model'
          label='Ai Model'
          onChange={onChange}
        >
          {models.map(opt => (
            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        type='number'
        onChange={onChange}
        value={values.topK}
        label='Top K'
        name='topK'
        sx={{
          mt: '24px',
        }}
        fullWidth
      />
    </KitModal>
  );
};

export default memo(SettingsModal);
